import { useToast } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import React from 'react';

import { EventLogTypeGQLType } from '../../../../types/dashboard-core-gqlTypes.generated.d';
import { PermissionGuard } from '~/core/PermissionGuard';
import { useCreateThreadWithCommentMutation } from '~/packages/comments/mutations/CreateThreadWithComment.gql.generated';
import { EventLogDrawer } from '~/packages/event-logs/EventLogDrawer';

// Constants
const EVENT_LOG_TYPES: EventLogTypeGQLType[] = [
  'fundingRequest_request_thread_create',
  'fundingRequest_request_canceled',
  'fundingRequest_request_expired',
  'fundingRequest_request_approved',
  'fundingRequest_request_unsubmitted',
  'fundingRequest_request_pending',
  'fundingRequest_request_rejected',
  'fundingRequest_request_submitted',
];
const SINGLE_ENTITY_DRAWER_LABEL = 'Funding Request Activity';
const MULTI_ENTITY_DRAWER_LABEL = 'Funding Request Activity';

// Exports
type SingleEntityDrawerProps = Partial<
  Omit<React.ComponentProps<typeof EventLogDrawer>, 'entityIds'>
> & {
  entityId: string;
};
export const SingleEntityDrawer = (props: SingleEntityDrawerProps) => {
  const toast = useToast();
  const [createThread] = useCreateThreadWithCommentMutation();
  return (
    <PermissionGuard
      permissions={(ctx) =>
        ctx.flags.productsFundingRequestsEventLogPermissions
      }
    >
      <EventLogDrawer
        {...props}
        entityThreadDomain="fundingRequest_request"
        entityIds={[props.entityId]}
        eventLogTypes={EVENT_LOG_TYPES}
        header={SINGLE_ENTITY_DRAWER_LABEL}
        triggerButtonProps={{ 'aria-label': SINGLE_ENTITY_DRAWER_LABEL }}
        onCreateThread={async ({ comment, participatingOrganizationIds }) => {
          try {
            const result = await createThread({
              variables: {
                comment,
                domain: 'fundingRequest_request',
                domainIdentifier: props.entityId,
                isPinned: false,
                participatingOrganizationIds,
              },
              refetchQueries: ['GetEventLogs'],
            });
            if (!result.data) throw Error('Failed to create thread');
          } catch (error) {
            Sentry.captureException(error);
            toast({
              title: 'Failed to create thread',
              status: 'error',
              position: 'top',
            });
          }
        }}
      />
    </PermissionGuard>
  );
};

// Exports
type MultiEntityDrawerProps = Partial<
  Omit<React.ComponentProps<typeof EventLogDrawer>, 'entityIds'>
>;
export const MultiEntityDrawer = (props: MultiEntityDrawerProps) => (
  <PermissionGuard
    permissions={(ctx) =>
      ctx.flags.productsFundingRequestsEventLogMultiEntityPermissions
    }
  >
    <EventLogDrawer
      {...props}
      entityIds={undefined}
      eventLogTypes={EVENT_LOG_TYPES}
      entityThreadDomain="fundingRequest_request"
      header={MULTI_ENTITY_DRAWER_LABEL}
      triggerButtonProps={{ 'aria-label': MULTI_ENTITY_DRAWER_LABEL }}
    />
  </PermissionGuard>
);
